import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { RotateCcw, X } from 'lucide-react';
import { post } from './api';
import type { UndoOffer, UndoResult } from './undo';

type ActiveUndo = UndoOffer & { onUndone?: (result: UndoResult) => void };

export function UndoHost() {
  const [offer, setOffer] = useState<ActiveUndo | null>(null);
  const [clock, setClock] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const receive = (event: Event) => { setOffer((event as CustomEvent<ActiveUndo>).detail); setClock(Date.now()); setError(''); setBusy(false); };
    window.addEventListener('nosivo:undo-offer', receive);
    return () => window.removeEventListener('nosivo:undo-offer', receive);
  }, []);
  useEffect(() => {
    if (!offer) return;
    const timer = window.setInterval(() => {
      const value = Date.now(); setClock(value);
      if (value >= new Date(offer.expiresAt).getTime()) setOffer(null);
    }, 100);
    return () => window.clearInterval(timer);
  }, [offer]);

  const remaining = useMemo(() => offer ? Math.max(0, new Date(offer.expiresAt).getTime() - clock) : 0, [clock, offer]);
  if (!offer) return null;

  async function undo() {
    if (!offer || busy) return;
    setBusy(true); setError('');
    try {
      const result = await post<UndoResult>(`/api/undo/${encodeURIComponent(offer.token)}`);
      offer.onUndone?.(result); setOffer(null);
    } catch (requestError) { setError((requestError as Error).message); setBusy(false); }
  }

  return <aside className="undo-toast" role="status" aria-live="polite">
    <span className="undo-toast__timer" style={{ '--undo-progress': `${remaining / 10000}` } as CSSProperties} />
    <div><strong>{error || offer.label}</strong><small>{error ? 'Refresh the page to see the latest state.' : `Undo available for ${Math.max(1, Math.ceil(remaining / 1000))} seconds`}</small></div>
    {!error && <button type="button" disabled={busy} onClick={() => void undo()}><RotateCcw size={15} />{busy ? 'Undoing…' : 'Undo'}</button>}
    <button type="button" className="undo-toast__close" aria-label="Dismiss undo" onClick={() => setOffer(null)}><X size={14} /></button>
  </aside>;
}
